"use client";

import { Suspense } from "react";
import dynamic from "next/dynamic";
const EnquiryForm = dynamic(
  () => import("@/components/forms/enquiry-form").then((mod) => mod.EnquiryForm),
  { ssr: false }
);
const RequestCallbackForm = dynamic(
  () => import("@/components/forms/request-callback-form").then((mod) => mod.RequestCallbackForm),
  { ssr: false }
);
import { Section } from "@/components/section";
import { SeoHeading } from "@/components/common/seo-heading";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Icon } from "@/components/common/icon";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { checkFirstString } from "@/helpers/email-phone-link";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

type QueriesProps = {
  section: PageSections;
  data?: {
    seoHeading?: string;
    heading?: string;
    subHeading?: string;
    buttonText?: string;
    buttonAction?: string;
    serviceId?: number;
    list?: {
      icon?: string;
      label?: string;
      value: string;
    }[];
  };
};

const Queries = ({ data, section }: QueriesProps) => {
  return (
    <Section id={section.sectionId} className={`${section.sectionClass}`}>
      <div className="grid items-center gap-5 sm:grid-cols-2">
        <div>
          <div className="pb-10">
            <SeoHeading heading={data?.heading} seoHeading={data?.seoHeading} />
            <Text>{data?.subHeading}</Text>
          </div>

          <ItemGroup className="gap-4" role={undefined}>
            {data?.list?.map((item) => (
              <Item
                key={item.label}
                variant="outline"
                role={undefined}
                render={
                  <a href={checkFirstString(item.value || "#")}>
                    <ItemMedia variant="image" className="bg-primary/10 text-primary">
                      <Icon name={getSystemIcon(item?.icon as string)} />
                    </ItemMedia>
                    <ItemContent>
                      <ItemTitle className="line-clamp-1">{item.label}</ItemTitle>
                      <ItemDescription>{item.value}</ItemDescription>
                    </ItemContent>
                  </a>
                }
              />
            ))}
          </ItemGroup>

          <Dialog>
            <DialogTrigger
              render={
                <Button size="lg" className="mt-6">
                  Request a callback
                </Button>
              }
            />
            <DialogContent className="p-0 sm:max-w-2xl">
              <Suspense>
                <RequestCallbackForm serviceId={data?.serviceId} />
              </Suspense>
            </DialogContent>
          </Dialog>
        </div>
        <Suspense>
          <EnquiryForm serviceId={data?.serviceId} />
        </Suspense>
      </div>
    </Section>
  );
};

export default Queries;
